import React, { useEffect, useState } from "react";
import { Bell, X, Sparkles, Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { isPushSupported, getPushPermission, subscribeToPush } from "../lib/push";
import { api } from "../lib/api";

/**
 * PushOptInPrompt (2 step)
 *  Step 1 — Attiva notifiche push (dopo 20s di navigazione)
 *  Step 2 — Chiedi l'email per completare l'iscrizione alla newsletter.
 *           Se l'utente prova a chiudere senza inserire l'email, mostro
 *           un messaggio che gli ricorda che così non riceverà le news via email.
 *
 *  Skipping rules:
 *   - Skippato se browser non supporta push
 *   - Skippato se utente già iscritto push E newsletter (localStorage flag)
 *   - Skippato se ha detto "Non ora" negli ultimi 3 giorni
 *   - Skippato per sempre se ha negato permessi (30 giorni snooze)
 */
const STORE_KEY_SNOOZE = "guzz_push_prompt_dismissed_until";
const STORE_KEY_NL_DONE = "guzz_newsletter_subscribed";
const DELAY_MS = 12_000;
const SNOOZE_DAYS = 3;
const DAY_MS = 24 * 60 * 60 * 1000;

export const PushOptInPrompt = () => {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(1); // 1 push, 2 email
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (!isPushSupported()) return;
    if (getPushPermission() === "denied") return;

    try {
      const until = parseInt(localStorage.getItem(STORE_KEY_SNOOZE) || "0", 10);
      if (until && Date.now() < until) return;
    } catch (_) {}

    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        const alreadyPush = !!sub;
        const alreadyNL = localStorage.getItem(STORE_KEY_NL_DONE) === "1";
        if (alreadyPush && alreadyNL) return; // completely enrolled → skip
        if (cancelled) return;
        setTimeout(() => {
          if (cancelled) return;
          setStep(alreadyPush ? 2 : 1); // if push already done, go straight to email
          setVisible(true);
        }, DELAY_MS);
      } catch (_) {}
    })();

    // External trigger: any component can call this to open the prompt on demand
    const onOpen = (e) => {
      const forceStep = e?.detail?.step;
      (async () => {
        try {
          const reg = await navigator.serviceWorker.ready;
          const sub = await reg.pushManager.getSubscription();
          setStep(forceStep || (sub ? 2 : 1));
        } catch {
          setStep(forceStep || 1);
        }
        setVisible(true);
      })();
    };
    window.addEventListener("open-push-optin", onOpen);
    return () => {
      cancelled = true;
      window.removeEventListener("open-push-optin", onOpen);
    };
  }, []);

  const snooze = (days = SNOOZE_DAYS) => {
    try {
      localStorage.setItem(STORE_KEY_SNOOZE, String(Date.now() + days * DAY_MS));
    } catch (_) {}
    setVisible(false);
  };

  const activatePush = async () => {
    setBusy(true);
    try {
      const { alreadySubscribed } = await subscribeToPush();
      toast.success(alreadySubscribed ? "Notifiche già attive" : "Notifiche attivate!");
      setStep(2);
    } catch (e) {
      const msg = String(e?.message || "");
      toast.error(msg || "Impossibile attivare");
      if (msg.includes("negato")) {
        snooze(30);
      }
    } finally {
      setBusy(false);
    }
  };

  const submitEmail = async (e) => {
    e?.preventDefault?.();
    const clean = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      toast.error("Inserisci un'email valida");
      return;
    }
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email: clean, source: "push_prompt" });
      try { localStorage.setItem(STORE_KEY_NL_DONE, "1"); } catch (_) {}
      toast.success("Iscrizione completata. Riceverai anche le email.");
      setVisible(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && String(detail).toLowerCase().includes("già")) {
        try { localStorage.setItem(STORE_KEY_NL_DONE, "1"); } catch (_) {}
        toast.info("Sei già iscritto con questa email");
        setVisible(false);
      } else {
        toast.error(detail || "Errore, riprova");
      }
    } finally {
      setBusy(false);
    }
  };

  // Prevent accidental close on step 2: remind about missing email
  const closeWithReminder = () => {
    if (step === 2) {
      const alreadyNL = localStorage.getItem(STORE_KEY_NL_DONE) === "1";
      if (!alreadyNL) {
        toast.warning(
          "Non hai lasciato la tua email: non riceverai le comunicazioni via posta. " +
          "Puoi iscriverti in qualsiasi momento dal footer della home.",
          { duration: 6500 }
        );
      }
    }
    snooze(SNOOZE_DAYS);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-4"
      data-testid="push-optin-prompt"
      role="dialog"
      aria-modal="true"
      aria-labelledby="push-optin-title"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={closeWithReminder}
      />
      <div className="relative w-full max-w-md bg-midnight border-2 border-gold shadow-2xl rounded-sm px-6 py-7 sm:px-8 sm:py-8">
        <button
          type="button"
          onClick={closeWithReminder}
          aria-label="Chiudi"
          className="absolute top-3 right-3 h-8 w-8 flex items-center justify-center text-white/50 hover:text-white transition-colors"
          data-testid="push-optin-dismiss"
        >
          <X size={18} />
        </button>

        {step === 1 && (
          <div className="flex flex-col items-center text-center">
            <div className="relative h-16 w-16 rounded-full bg-gold flex items-center justify-center mb-5">
              <Bell size={30} className="text-midnight" strokeWidth={2.5} />
              <span className="absolute inset-0 rounded-full ring-2 ring-gold/60 animate-ping" />
            </div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-gold font-semibold mb-3">
              <Sparkles size={11} /> Passo 1 di 2
            </div>
            <h3 id="push-optin-title" className="font-display text-2xl sm:text-3xl text-white leading-tight mb-3">
              Attiva le <span className="italic text-gold">notifiche</span>
            </h3>
            <p className="text-sm sm:text-base text-white/75 leading-relaxed mb-6">
              Ti avviso appena arriva una <span className="text-gold font-semibold">nuova auto</span> o una
              <span className="text-gold font-semibold"> promozione</span> — prima di tutti gli altri.
              Nessuno spam, giuriamo.
            </p>
            <button
              type="button"
              onClick={activatePush}
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 py-4 px-6 bg-gold text-midnight text-sm font-black uppercase tracking-wider hover:bg-yellow-300 transition-colors rounded-sm shadow-lg disabled:opacity-50"
              data-testid="push-optin-activate"
            >
              <Bell size={16} strokeWidth={3} />
              {busy ? "Attivazione..." : "Attiva notifiche"}
            </button>
            <button
              type="button"
              onClick={closeWithReminder}
              className="mt-3 text-xs text-white/50 hover:text-white/80 transition-colors uppercase tracking-widest"
              data-testid="push-optin-snooze"
            >
              Non ora
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="flex flex-col items-center text-center">
            <div className="relative h-16 w-16 rounded-full bg-gold flex items-center justify-center mb-5">
              <Mail size={28} className="text-midnight" strokeWidth={2.5} />
              <span className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-midnight border-2 border-gold flex items-center justify-center">
                <Check size={12} className="text-gold" strokeWidth={3} />
              </span>
            </div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-gold font-semibold mb-3">
              <Sparkles size={11} /> Passo 2 di 2
            </div>
            <h3 className="font-display text-2xl sm:text-3xl text-white leading-tight mb-2">
              Manca l'<span className="italic text-gold">email</span>
            </h3>
            <p className="text-sm sm:text-base text-white/75 leading-relaxed mb-5">
              Lascia la tua email per <span className="text-gold font-semibold">ricevere le news</span> anche
              quando il telefono è spento o il browser bloccato.
              <br />
              <span className="text-white/55 text-xs">Senza email non riceverai le comunicazioni via posta.</span>
            </p>
            <form onSubmit={submitEmail} className="w-full">
              <input
                type="email"
                required
                autoFocus
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="latuaemail@esempio.com"
                className="w-full px-4 py-3.5 text-sm bg-white text-midnight border-2 border-gold rounded-sm mb-3 outline-none focus:ring-2 focus:ring-gold/50 font-semibold"
                data-testid="push-optin-email-input"
              />
              <button
                type="submit"
                disabled={busy || !email.trim()}
                className="w-full flex items-center justify-center gap-2 py-4 px-6 bg-gold text-midnight text-sm font-black uppercase tracking-wider hover:bg-yellow-300 transition-colors rounded-sm shadow-lg disabled:opacity-50"
                data-testid="push-optin-email-submit"
              >
                <Mail size={16} strokeWidth={3} />
                {busy ? "Iscrizione..." : "Iscrivimi alla newsletter"}
              </button>
            </form>
            <button
              type="button"
              onClick={closeWithReminder}
              className="mt-3 text-xs text-white/50 hover:text-white/80 transition-colors uppercase tracking-widest"
              data-testid="push-optin-email-skip"
            >
              Salta questo passo
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PushOptInPrompt;
